import { IErrorDetails } from '@cian/peperrors/shared';
import { format, startOfToday } from 'date-fns';

import {
  EDailyrentBookingCalendarActionTypes,
  TBookingAvailabilityData,
} from 'shared/actions/dailyrentBookingCalendar/types';
import { ResponseError } from 'shared/errors/responseError';
import { ICheckPriceRequest } from 'shared/repositories/dailyrent/v1/check-price';
import { selectDailyrentBookingCalendarDates } from 'shared/selectors/dailyrentBookingCalendar';
import { selectOfferId } from 'shared/selectors/offerData/offer/selectOfferId';
import { fetchCheckPrice } from 'shared/services/fetchCheckPrice';
import { DEFAULT_ERROR_MESSAGE, fetchGetOfferCalendar } from 'shared/services/fetchGetOfferCalendar';
import { IThunkActionCreator } from 'shared/store';
import { TDaysAvailability } from 'shared/types/bookingCalendar';
import { IOfferData } from 'shared/types/offerData';
import { actionGenerator } from 'shared/utils/redux/actionGenerator';

export const toggleCalendarModal = actionGenerator<
  EDailyrentBookingCalendarActionTypes.ToggleCalendarModal,
  boolean | void
>(EDailyrentBookingCalendarActionTypes.ToggleCalendarModal);

export const setDaysAvailability = actionGenerator<
  EDailyrentBookingCalendarActionTypes.SetDaysAvailability,
  TDaysAvailability
>(EDailyrentBookingCalendarActionTypes.SetDaysAvailability);

export const setInitialLoading = actionGenerator<EDailyrentBookingCalendarActionTypes.SetInitialLoading, boolean>(
  EDailyrentBookingCalendarActionTypes.SetInitialLoading,
);

export const setAvailabilityLoading = actionGenerator<
  EDailyrentBookingCalendarActionTypes.SetAvailabilityLoading,
  boolean
>(EDailyrentBookingCalendarActionTypes.SetAvailabilityLoading);

export const setError = actionGenerator<EDailyrentBookingCalendarActionTypes.SetError, string>(
  EDailyrentBookingCalendarActionTypes.SetError,
);

export const setDates = actionGenerator<
  EDailyrentBookingCalendarActionTypes.SetDates,
  { from: string | undefined; to: string | undefined }
>(EDailyrentBookingCalendarActionTypes.SetDates);

export const setBookingCalendarDataFromOffer = actionGenerator<
  EDailyrentBookingCalendarActionTypes.SetBookingCalendarDataFromOffer,
  IOfferData
>(EDailyrentBookingCalendarActionTypes.SetBookingCalendarDataFromOffer);

export const setBookingCalendarAvailabilityData = actionGenerator<
  EDailyrentBookingCalendarActionTypes.SetBookingCalendarAvailabilityData,
  TBookingAvailabilityData
>(EDailyrentBookingCalendarActionTypes.SetBookingCalendarAvailabilityData);

export const setCanBeBooked = actionGenerator<EDailyrentBookingCalendarActionTypes.SetDailyrentCanBeBooked, boolean>(
  EDailyrentBookingCalendarActionTypes.SetDailyrentCanBeBooked,
);

/** Запрашивает календарь доступности */
export const loadCalendarDaysAvailability =
  (): IThunkActionCreator<Promise<void>> => async (dispatch, getState, context) => {
    const state = getState();
    const offerRealtyId = selectOfferId(state);
    const today = startOfToday();
    const startDate = format(today, 'yyyy-MM-dd');

    dispatch(setInitialLoading(true));

    try {
      const { calendar } = await fetchGetOfferCalendar(context, { offerRealtyId, startDate });
      dispatch(setDaysAvailability(calendar));
    } catch (err) {
      dispatch(setError(err.message));
    } finally {
      dispatch(setInitialLoading(false));
    }
  };

/** Проверяет доступность */
export const checkAvailability =
  ({ dates }: Omit<ICheckPriceRequest, 'offerRealtyId'>): IThunkActionCreator<Promise<void | string>> =>
  async (dispatch, getState, context) => {
    const state = getState();
    const offerRealtyId = selectOfferId(state);

    dispatch(setAvailabilityLoading(true));

    try {
      const availabilityTitles = await fetchCheckPrice(context, {
        offerRealtyId,
        dates,
      });
      const bookingAvailabilityData: TBookingAvailabilityData = {
        dateInputText: availabilityTitles.dateInputText,
        defaultBookingMessage: availabilityTitles.onlineBookingMessage || '',
        price: availabilityTitles.mainPrice,
      };
      dispatch(setBookingCalendarAvailabilityData(bookingAvailabilityData));
    } catch (error) {
      let errorMessage = DEFAULT_ERROR_MESSAGE;

      if (error instanceof ResponseError && (error.details as IErrorDetails).code === 'minStay') {
        errorMessage = error.message;
      }

      dispatch(setError(errorMessage));
      dispatch(setCanBeBooked(false));

      return errorMessage;
    } finally {
      dispatch(setAvailabilityLoading(false));
    }

    return;
  };

export const openBookingCalendar = (): IThunkActionCreator<Promise<void>> => async (dispatch, getState) => {
  const state = getState();
  const dates = selectDailyrentBookingCalendarDates(state);
  const { from, to } = dates;

  dispatch(toggleCalendarModal(true));
  await dispatch(loadCalendarDaysAvailability());
  if (from && to) {
    dispatch(checkAvailability({ dates: { start: from, end: to } }));
  }
};
